import { db } from '../database/firebase';
import { collection, getDocs, query, orderBy, limit, where } from 'firebase/firestore';
import { getManagerApiFilters } from '../utils/eventListUtils';
import { withEventSlugAsRaId } from '../utils/eventIdentity';
import { getPanelAccessToken } from './apiAuthProvider';

const DEFAULT_PAGE_SIZE = 100;
const DEFAULT_ORDER_ON = 'startdate';
const DEFAULT_ORDER = 'asc';
const EVENTS_COLLECTION = 'events';

const useApiData = process.env.REACT_APP_USE_API_DATA === 'true';

const getApiUrl = () => {
  const base = process.env.REACT_APP_API_URL ?? 'http://localhost:3001';
  return base.replace(/\/$/, '');
};

/**
 * NestJS API'den durum (active|past|upcoming) filtreli etkinlik listesi.
 * @returns {{ data: Array, total: number, page: number, perPage: number }}
 */
const getEventsByStatusFromApi = async (
  status,
  {
    sort = 'startdate',
    order = 'asc',
    page = 1,
    perPage = DEFAULT_PAGE_SIZE,
    apiFilters = {},
  } = {},
) => {
  const safePage = Math.max(1, Number(page) || 1);
  const safePerPage = Math.max(1, Number(perPage) || DEFAULT_PAGE_SIZE);
  const params = new URLSearchParams({
    page: String(safePage),
    perPage: String(safePerPage),
    status,
    sort,
    order,
    // Panel list: satır satır stats/:id (canlı satış tarama) yerine tek batch revenue
    includeRevenue: 'true',
  });
  Object.entries(apiFilters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  });
  const token = getPanelAccessToken();
  const response = await fetch(`${getApiUrl()}/events?${params.toString()}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch events (${response.status})`);
  }
  const json = await response.json();
  return {
    data: (json.data ?? []).map(withEventSlugAsRaId),
    total: json.total ?? 0,
    page: json.page ?? safePage,
    perPage: json.perPage ?? safePerPage,
  };
};

export const getActiveEvents = async (permissions, options = {}) => {
  if (useApiData) {
    return getEventsByStatusFromApi('active', {
      sort: 'startdate',
      order: 'asc',
      page: options.page,
      perPage: options.perPage,
      apiFilters: {
        ...getManagerApiFilters(permissions),
        ...(options.apiFilters || {}),
      },
    });
  }

  const collectionRef = collection(db, EVENTS_COLLECTION);

  const isOrgManager = permissions?.crole === 'organisationCompanyManager' && !!permissions?.rid;
  const isVenueManager = permissions?.crole === 'venueManager' && !!permissions?.rid;

  let q;
  if (isOrgManager) {
    q = query(
      collectionRef,
      orderBy(DEFAULT_ORDER_ON, DEFAULT_ORDER),
      limit(DEFAULT_PAGE_SIZE),
      where('startdate', '>=', new Date()),
      where('organisationCompanies', 'array-contains', permissions.rid),
    );
  } else if (isVenueManager) {
    q = query(
      collectionRef,
      orderBy(DEFAULT_ORDER_ON, DEFAULT_ORDER),
      limit(DEFAULT_PAGE_SIZE),
      where('startdate', '>=', new Date()),
      where('venue', '==', permissions.rid),
    );
  } else {
    q = query(
      collectionRef,
      orderBy(DEFAULT_ORDER_ON, DEFAULT_ORDER),
      limit(DEFAULT_PAGE_SIZE),
      where('startdate', '<=', new Date()),
    );
  }

  const snapshot = await getDocs(q);
  const rows = [];
  snapshot?.forEach((docRef) => {
    const docData = docRef.data();
    if (docData?.enddate?.toDate && docData.enddate.toDate() >= new Date()) {
      rows.push({ id: docRef.id, ...docData });
    }
  });

  return { data: rows, total: rows.length, page: 1, perPage: DEFAULT_PAGE_SIZE };
};

export const getPastEvents = async (permissions, options = {}) => {
  if (useApiData) {
    return getEventsByStatusFromApi('past', {
      sort: 'enddate',
      order: 'desc',
      page: options.page,
      perPage: options.perPage,
      apiFilters: {
        ...getManagerApiFilters(permissions),
        ...(options.apiFilters || {}),
      },
    });
  }

  const collectionRef = collection(db, EVENTS_COLLECTION);

  const isOrgManager = permissions?.crole === 'organisationCompanyManager' && !!permissions?.rid;
  const isVenueManager = permissions?.crole === 'venueManager' && !!permissions?.rid;

  let q;
  if (isOrgManager) {
    q = query(
      collectionRef,
      orderBy('enddate', 'desc'),
      limit(DEFAULT_PAGE_SIZE),
      where('enddate', '<', new Date()),
      where('organisationCompanies', 'array-contains', permissions.rid),
    );
  } else if (isVenueManager) {
    q = query(
      collectionRef,
      orderBy('enddate', 'desc'),
      limit(DEFAULT_PAGE_SIZE),
      where('enddate', '<', new Date()),
      where('venue', '==', permissions.rid),
    );
  } else {
    q = query(
      collectionRef,
      orderBy('enddate', 'desc'),
      limit(DEFAULT_PAGE_SIZE),
      where('enddate', '<', new Date()),
    );
  }

  const snapshot = await getDocs(q);
  const rows = [];
  snapshot?.forEach((docRef) => {
    const docData = docRef.data();
    rows.push({ id: docRef.id, ...docData });
  });

  return { data: rows, total: rows.length, page: 1, perPage: DEFAULT_PAGE_SIZE };
};

const eventsService = {
  getActiveEvents,
  getPastEvents,
};

export default eventsService;
